<template>
  <Public>
    <v-main>
      <v-container fluid>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="10">
            <v-card class="tw-my-8" flat>
              <v-card-title class="tw-text-2xl tw-font-bold">Privacy Policy</v-card-title>
              <v-card-text class="text--primary tw-prose tw-prose-sm tw-max-w-none">

                <p class="tw-text-gray-600 tw-mb-6">Effective Date: November 25, 2025</p>

                <h2>1. Introduction and Scope</h2>
                <p>
                  This Privacy Policy ("Policy") is issued by Sala Daeng Company Limited, a company duly incorporated
                  and existing under the laws of the Kingdom of Thailand, with its registered office located at
                  6/F, Q House, 598 Phloen Chit Road, Lumphini Sub-district, Pathum Wan District, Bangkok 10330, Thailand
                  (hereinafter referred to as "Company," "we," "us," or "our").
                </p>
                <p>
                  This Policy governs the collection, use, storage, disclosure, and protection of personal data
                  obtained through your access to and use of our website located at phasicharoen.com and the
                  PhasiCharoen application (collectively referred to as the "Services"). This Policy applies to
                  all users, visitors, and any other individuals who access or utilize our Services.
                </p>
                <p>
                  By accessing, browsing, or otherwise using our Services, you acknowledge that you have read,
                  understood, and agree to be bound by the terms and conditions set forth in this Policy.
                  If you do not agree with any provision of this Policy, you must immediately discontinue
                  your use of the Services. Your continued use of the Services following the posting of any
                  amendments to this Policy shall constitute your acceptance of such amendments.
                </p>
                <p>
                  We are committed to safeguarding your privacy and ensuring the protection of your personal
                  data in accordance with the Thailand Personal Data Protection Act B.E. 2562 (2019) ("PDPA")
                  and other applicable data protection laws and regulations.
                </p>

                <h2>2. Definitions and Interpretation</h2>
                <p>For the purposes of this Policy, the following terms shall have the meanings ascribed to them below:</p>
                <ul>
                  <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person, whether directly or indirectly, but excluding information of deceased persons.</li>
                  <li><strong>"Processing"</strong> means any operation or set of operations performed on personal data, whether or not by automated means, including collection, recording, organization, structuring, storage, adaptation, alteration, retrieval, consultation, use, disclosure, dissemination, alignment, combination, restriction, erasure, or destruction.</li>
                  <li><strong>"Data Controller"</strong> means the Company, which determines the purposes and means of the processing of personal data.</li>
                  <li><strong>"Data Subject"</strong> means the individual to whom the personal data relates.</li>
                  <li><strong>"Third Party"</strong> means any natural or legal person, public authority, agency, or body other than the data subject, the data controller, and persons authorized to process personal data under the direct authority of the data controller.</li>
                </ul>

                <h2>3. Categories of Personal Data Collected</h2>
                <h3>3.1 Personal Data Provided Directly by the Data Subject</h3>
                <p>
                  In the course of your interaction with our Services, we may collect personal data that you
                  voluntarily provide to us. Such data may include, but is not limited to:
                </p>
                <ul>
                  <li><strong>Identity Information:</strong> Full legal name, including first name, middle name (if applicable), and surname; title or honorific prefix; date of birth; gender; nationality; government-issued identification numbers (such as national identification number, passport number, or tax identification number).</li>
                  <li><strong>Contact Information:</strong> Residential address, mailing address, email address, telephone number (including mobile and landline), facsimile number, and other contact details.</li>
                  <li><strong>Account Credentials:</strong> Username, password, security questions and answers, and other authentication information used to access our Services.</li>
                  <li><strong>Financial Information:</strong> Bank account details, credit card information, billing address, payment history, and transaction records, where applicable to the Services provided.</li>
                  <li><strong>Professional Information:</strong> Occupation, employer name, business address, job title, professional qualifications, and work-related contact information.</li>
                  <li><strong>Communication Records:</strong> Correspondence, inquiries, feedback, complaints, and other communications exchanged between you and the Company.</li>
                </ul>

                <h3>3.2 Personal Data Collected Automatically</h3>
                <p>
                  When you access or use our Services, certain information may be collected automatically
                  through technological means. This automatically collected data includes:
                </p>
                <ul>
                  <li><strong>Device Information:</strong> Hardware model, device type, operating system type and version, unique device identifiers (including IMEI, MAC address, and advertising identifiers), screen resolution, and device settings.</li>
                  <li><strong>Technical Data:</strong> Internet Protocol (IP) address, browser type and version, browser plug-in types and versions, time zone setting, language preferences, and other technology on the devices used to access the Services.</li>
                  <li><strong>Usage Data:</strong> Information about how you interact with our Services, including pages visited, features used, links clicked, time spent on pages, search queries, navigation paths, and frequency and duration of visits.</li>
                  <li><strong>Log Data:</strong> Server logs that record information such as access times, referring URLs, error logs, and other diagnostic data.</li>
                  <li><strong>Cookies and Similar Technologies:</strong> Data collected through cookies, web beacons, pixel tags, and other similar tracking technologies, as further described in Section 10 of this Policy.</li>
                </ul>

                <h2>4. Purposes of Data Processing</h2>
                <p>
                  The Company processes your personal data for specific, explicit, and legitimate purposes.
                  We shall not further process personal data in a manner incompatible with those purposes.
                  The purposes for which we process your personal data include:
                </p>
                <h3>4.1 Service Provision and Contract Performance</h3>
                <ul>
                  <li>To establish, maintain, and manage your account and provide you with access to the Services;</li>
                  <li>To process, fulfill, and deliver orders, transactions, and requests made through the Services;</li>
                  <li>To provide customer support, respond to inquiries, and address complaints or disputes;</li>
                  <li>To verify your identity and authenticate your access to the Services;</li>
                  <li>To send you service-related communications, including confirmations, invoices, technical notices, updates, security alerts, and administrative messages.</li>
                </ul>
                <h3>4.2 Legitimate Business Interests</h3>
                <ul>
                  <li>To maintain, improve, and optimize the functionality, performance, and user experience of our Services;</li>
                  <li>To conduct research, analysis, and statistical studies to understand user behavior and preferences;</li>
                  <li>To develop new products, services, features, and functionalities;</li>
                  <li>To protect the security, integrity, and availability of our Services and infrastructure;</li>
                  <li>To detect, prevent, investigate, and address fraud, unauthorized access, illegal activities, and other security incidents;</li>
                  <li>To enforce our terms of service, acceptable use policies, and other contractual obligations.</li>
                </ul>
                <h3>4.3 Legal Compliance</h3>
                <ul>
                  <li>To comply with applicable laws, regulations, legal processes, and governmental requests;</li>
                  <li>To establish, exercise, or defend legal claims and rights;</li>
                  <li>To fulfill statutory record-keeping, reporting, and disclosure obligations;</li>
                  <li>To respond to lawful requests from law enforcement authorities and regulatory bodies.</li>
                </ul>

                <h2>5. Legal Basis for Processing</h2>
                <p>
                  In accordance with applicable data protection laws, including the PDPA, the Company processes
                  your personal data based on one or more of the following lawful grounds:
                </p>
                <ul>
                  <li><strong>Consent:</strong> Where you have given explicit consent to the processing of your personal data for one or more specific purposes. You have the right to withdraw your consent at any time, without affecting the lawfulness of processing based on consent before its withdrawal.</li>
                  <li><strong>Contractual Necessity:</strong> Where processing is necessary for the performance of a contract to which you are a party, or to take steps at your request prior to entering into a contract.</li>
                  <li><strong>Legal Obligation:</strong> Where processing is necessary for compliance with a legal obligation to which the Company is subject.</li>
                  <li><strong>Vital Interests:</strong> Where processing is necessary to protect the vital interests of the data subject or another natural person.</li>
                  <li><strong>Legitimate Interests:</strong> Where processing is necessary for the purposes of the legitimate interests pursued by the Company or a third party, except where such interests are overridden by the fundamental rights and freedoms of the data subject.</li>
                  <li><strong>Public Interest:</strong> Where processing is necessary for the performance of a task carried out in the public interest or in the exercise of official authority vested in the Company.</li>
                </ul>

                <h2>6. Disclosure and Transfer of Personal Data</h2>
                <p>
                  The Company may disclose or transfer your personal data to third parties under the following
                  circumstances and subject to appropriate safeguards:
                </p>
                <h3>6.1 Service Providers and Business Partners</h3>
                <p>
                  We may engage third-party service providers, contractors, agents, and business partners to
                  perform services on our behalf. These entities may have access to your personal data only
                  to the extent necessary to perform their designated functions and are contractually obligated
                  to maintain the confidentiality and security of such data. Categories of service providers include:
                </p>
                <ul>
                  <li>Cloud computing and data hosting providers;</li>
                  <li>Payment processors and financial institutions;</li>
                  <li>Customer relationship management providers;</li>
                  <li>Analytics and data processing services;</li>
                  <li>Communication and notification services;</li>
                  <li>Professional advisors, including lawyers, accountants, and auditors.</li>
                </ul>
                <h3>6.2 Legal and Regulatory Disclosures</h3>
                <p>
                  We may disclose your personal data where required or permitted by law, including to:
                </p>
                <ul>
                  <li>Comply with legal obligations, court orders, subpoenas, or other legal processes;</li>
                  <li>Respond to lawful requests from governmental authorities, law enforcement agencies, or regulatory bodies;</li>
                  <li>Protect and defend our rights, property, or safety, or those of our users or third parties;</li>
                  <li>Investigate potential violations of our terms of service or applicable laws.</li>
                </ul>
                <h3>6.3 Business Transactions</h3>
                <p>
                  In the event of a merger, acquisition, reorganization, bankruptcy, receivership, sale of assets,
                  or similar corporate transaction, your personal data may be transferred to the acquiring entity
                  or successor organization as part of the transaction, subject to the continuation of this Policy
                  or the adoption of a comparable privacy policy by the acquiring party.
                </p>
                <h3>6.4 Cross-Border Data Transfers</h3>
                <p>
                  Your personal data may be transferred to, stored, or processed in countries other than the
                  country in which you reside. When we transfer personal data internationally, we implement
                  appropriate safeguards to ensure that your data receives an adequate level of protection
                  in accordance with applicable data protection laws.
                </p>

                <h2>7. Data Security Measures</h2>
                <p>
                  The Company implements and maintains appropriate technical and organizational security measures
                  designed to protect personal data against accidental or unlawful destruction, loss, alteration,
                  unauthorized disclosure, or access. These measures include, but are not limited to:
                </p>
                <ul>
                  <li><strong>Encryption:</strong> Use of industry-standard encryption protocols (such as TLS/SSL) for data transmitted over networks and encryption of sensitive data at rest;</li>
                  <li><strong>Access Controls:</strong> Implementation of role-based access controls, authentication mechanisms, and authorization procedures to restrict access to personal data to authorized personnel only;</li>
                  <li><strong>Physical Security:</strong> Deployment of physical security measures at data centers and facilities, including access controls, surveillance systems, and environmental controls;</li>
                  <li><strong>Network Security:</strong> Use of firewalls, intrusion detection and prevention systems, and regular security monitoring and vulnerability assessments;</li>
                  <li><strong>Employee Training:</strong> Regular training and awareness programs for employees regarding data protection obligations and security best practices;</li>
                  <li><strong>Incident Response:</strong> Establishment of incident response procedures to detect, investigate, and respond to security breaches and data incidents.</li>
                </ul>
                <p>
                  Notwithstanding the foregoing, no method of transmission over the Internet or method of
                  electronic storage is completely secure. While we strive to protect your personal data
                  using commercially reasonable means, we cannot guarantee the absolute security of your data.
                  You acknowledge and accept the inherent security risks of providing information over the
                  Internet and agree not to hold us responsible for any breach of security unless such breach
                  is due to our gross negligence or willful misconduct.
                </p>

                <h2>8. Data Retention</h2>
                <p>
                  The Company retains personal data only for as long as necessary to fulfill the purposes for
                  which it was collected, including to satisfy any legal, regulatory, accounting, or reporting
                  requirements. The retention period is determined based on the following criteria:
                </p>
                <ul>
                  <li>The nature, scope, and purposes of the processing;</li>
                  <li>The amount and sensitivity of the personal data;</li>
                  <li>The potential risk of harm from unauthorized use or disclosure;</li>
                  <li>Whether the purposes of processing can be achieved through other means;</li>
                  <li>Applicable legal, regulatory, and contractual requirements.</li>
                </ul>
                <p>
                  Upon expiration of the applicable retention period, or upon your valid request for deletion
                  (subject to legal exceptions), we will securely delete or anonymize your personal data in
                  accordance with our data retention and destruction policies. Anonymized data that cannot
                  be used to identify you may be retained indefinitely for statistical, research, or analytical purposes.
                </p>

                <h2>9. Rights of Data Subjects</h2>
                <p>
                  Subject to applicable laws and regulations, including the PDPA, you may have the following
                  rights with respect to your personal data:
                </p>
                <ul>
                  <li><strong>Right of Access:</strong> You have the right to request confirmation as to whether we are processing your personal data, and if so, to obtain access to such data along with information about the purposes of processing, categories of data, recipients, retention periods, and your rights.</li>
                  <li><strong>Right to Rectification:</strong> You have the right to request the correction, completion, or updating of any inaccurate, incomplete, or outdated personal data concerning you.</li>
                  <li><strong>Right to Erasure:</strong> You have the right to request the deletion or removal of your personal data where there is no compelling reason for its continued processing, subject to legal exceptions and retention requirements.</li>
                  <li><strong>Right to Restriction:</strong> You have the right to request the restriction of processing of your personal data under certain circumstances, such as when you contest the accuracy of the data or object to the processing.</li>
                  <li><strong>Right to Data Portability:</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format, and to transmit such data to another controller without hindrance, where technically feasible.</li>
                  <li><strong>Right to Object:</strong> You have the right to object to the processing of your personal data on grounds relating to your particular situation, where such processing is based on legitimate interests or is carried out for direct marketing purposes.</li>
                  <li><strong>Right to Withdraw Consent:</strong> Where processing is based on your consent, you have the right to withdraw such consent at any time, without affecting the lawfulness of processing based on consent before its withdrawal.</li>
                  <li><strong>Right to Lodge a Complaint:</strong> You have the right to lodge a complaint with the relevant supervisory authority if you believe that the processing of your personal data violates applicable data protection laws.</li>
                </ul>
                <p>
                  To exercise any of the foregoing rights, please submit a written request to us using the
                  contact information provided in Section 15 of this Policy. We may require you to verify
                  your identity before processing your request. We will respond to your request within the
                  timeframe prescribed by applicable law, and in any event, within thirty (30) days of receipt
                  of the request, unless an extension is permitted under applicable law.
                </p>

                <h2>10. Cookies and Tracking Technologies</h2>
                <p>
                  Our Services utilize cookies and similar tracking technologies to collect and store information
                  about your interactions with our Services. Cookies are small text files that are placed on
                  your device when you visit a website. We use the following types of cookies:
                </p>
                <ul>
                  <li><strong>Strictly Necessary Cookies:</strong> These cookies are essential for the operation of our Services and enable you to navigate and use the features of the Services. Without these cookies, certain functionalities cannot be provided.</li>
                  <li><strong>Performance and Analytics Cookies:</strong> These cookies collect information about how visitors use our Services, including which pages are visited most frequently, how users navigate between pages, and whether error messages are received. This information is used to improve the performance and user experience of our Services.</li>
                  <li><strong>Functionality Cookies:</strong> These cookies allow our Services to remember choices you make (such as language preferences or region) and provide enhanced, personalized features.</li>
                </ul>
                <p>
                  You may manage your cookie preferences through your browser settings. Most web browsers allow
                  you to control cookies through their settings preferences. You can configure your browser to
                  refuse all cookies, accept only certain cookies, or notify you when a cookie is being set.
                  Please note that if you disable or refuse cookies, some features of our Services may become
                  inaccessible or may not function properly.
                </p>

                <h2>11. Analytics Services</h2>
                <p>
                  We utilize third-party analytics services, including Google Analytics, to help us understand
                  and analyze how users interact with our Services. Google Analytics uses cookies and similar
                  technologies to collect and analyze information about the use of our Services and to compile
                  reports on website activity and user behavior.
                </p>
                <p>
                  The information generated by cookies about your use of our Services is generally transmitted
                  to and stored by Google on servers located outside of Thailand. Google may use this information
                  to evaluate your use of the Services, compile reports on website activity for website operators,
                  and provide other services relating to website activity and Internet usage.
                </p>
                <p>
                  For more information about Google's data practices, please review Google's Privacy Policy at
                  <a href="https://www.google.com/policies/privacy/" target="_blank" rel="noopener">https://www.google.com/policies/privacy/</a>
                  and Google's information page on how Google uses data when you use partner sites or apps at
                  <a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener">https://www.google.com/policies/privacy/partners/</a>.
                  You may opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on,
                  available at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener">https://tools.google.com/dlpage/gaoptout</a>.
                </p>

                <h2>12. Third-Party Links and Services</h2>
                <p>
                  Our Services may contain hyperlinks to websites, applications, or services operated by third
                  parties that are not under the control of the Company. This Policy does not apply to such
                  third-party websites, applications, or services. We have no control over, and assume no
                  responsibility for, the content, privacy policies, data practices, or conduct of any third party.
                </p>
                <p>
                  We strongly encourage you to review the privacy policies and terms of service of any
                  third-party websites or services before providing any personal data or engaging in any
                  transactions with such third parties. Your interactions with third-party websites and
                  services are solely between you and the third party, and we disclaim any liability arising
                  from such interactions.
                </p>

                <h2>13. Children's Privacy</h2>
                <p>
                  Our Services are not directed to, and are not intended for use by, individuals under the age
                  of twenty (20) years without the consent of a parent or legal guardian, or individuals under
                  the age of thirteen (13) years under any circumstances. We do not knowingly collect, solicit,
                  or maintain personal data from children under the age of thirteen (13).
                </p>
                <p>
                  If you are a parent or legal guardian and become aware that your child has provided personal
                  data to us without your consent, please contact us immediately using the contact information
                  provided in Section 15. Upon verification, we will take prompt steps to delete such personal
                  data from our systems and records.
                </p>

                <h2>14. Amendments to This Policy</h2>
                <p>
                  The Company reserves the right to modify, amend, or update this Policy at any time at its
                  sole discretion. Any changes to this Policy will be effective immediately upon posting the
                  revised Policy on our Services, with an updated "Effective Date" at the top of this Policy.
                </p>
                <p>
                  We encourage you to periodically review this Policy to stay informed about our data practices
                  and privacy protection measures. Your continued use of the Services following the posting
                  of any amendments to this Policy shall constitute your acknowledgment and acceptance of
                  such amendments. If you do not agree with any changes to this Policy, you must discontinue
                  your use of the Services.
                </p>
                <p>
                  For material changes that significantly affect your rights or our processing activities,
                  we may provide additional notice through email notification or a prominent notice on our
                  Services prior to the changes taking effect.
                </p>

                <h2>15. Contact Information</h2>
                <p>
                  If you have any questions, concerns, or requests regarding this Policy, our privacy practices,
                  or the processing of your personal data, please contact us through the following channels:
                </p>
                <ul>
                  <li><strong>Data Controller:</strong> Sala Daeng Company Limited</li>
                  <li><strong>Address:</strong> 6/F, Q House, 598 Phloen Chit Road, Lumphini Sub-district, Pathum Wan District, Bangkok 10330, Thailand</li>
                  <li><strong>Email:</strong> hello@phasicharoen.com</li>
                </ul>
                <p>
                  We will endeavor to respond to all legitimate inquiries within a reasonable timeframe and
                  in accordance with applicable legal requirements.
                </p>

                <h2>16. Governing Law and Jurisdiction</h2>
                <p>
                  This Policy shall be governed by and construed in accordance with the laws of the Kingdom
                  of Thailand, without regard to its conflict of law principles. Any dispute arising out of
                  or in connection with this Policy, including any question regarding its existence, validity,
                  or termination, shall be subject to the exclusive jurisdiction of the courts of Thailand.
                </p>

              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </Public>
</template>

<script>
import Public from '@/components/public/public-main-template'

export default {
  name: 'PrivacyPolicy',
  components: {
    Public,
  }
}
</script>

<style lang="scss" scoped>
h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: #333;
}

h3 {
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #444;
}

ul {
  margin-bottom: 1rem;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;
  }
}

p {
  margin-bottom: 1rem;
  line-height: 1.6;
}

a {
  color: #1976d2;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
